/* ============================================================
   PALAMANO — Tablero de frases (split-flap)

   Cada letra es una tarjeta que gira, como los tableros de los
   aeropuertos: pasa por unas cuantas letras sueltas antes de
   quedarse en la suya. Las frases van en el editor del tema.

   Si el visitante pidió menos movimiento en su sistema, la frase
   cambia sin más, sin girar nada.
   ============================================================ */
(() => {
  const ALFABETO = 'ABCDEFGHIJKLMNÑOPQRSTUVWXYZÁÉÍÓÚ ';
  const GIRO = 48;      // lo que tarda media vuelta
  const PASOS = 3;      // letras sueltas antes de la buena
  const RETRASO = 16;   // lo que espera cada tarjeta respecto a la anterior

  const dormir = (ms) => new Promise((listo) => setTimeout(listo, ms));

  class PalamanoFlap extends HTMLElement {
    connectedCallback() {
      const plantillas = [...this.querySelectorAll('template[data-frase]')]
        .filter((t) => t.content.textContent.trim());

      this.frases = plantillas.map((t) => t.content.textContent.trim().toUpperCase());
      this.autores = plantillas.map((t) => t.dataset.autor || '');

      if (this.frases.length === 0) return;

      this.tablero = document.createElement('span');
      this.tablero.className = 'pmn-flap__tablero';
      this.append(this.tablero);

      /* La línea del autor existe siempre, aunque esté vacía: así el título
         de la colección no se mueve al pasar de una frase con autor a otra
         sin él. */
      this.pie = document.createElement('span');
      this.pie.className = 'pmn-flap__autor';
      this.append(this.pie);

      this.quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.actual = 0;
      this.pintar(this.frases[0]);
      this.firmar(0);

      if (this.frases.length === 1) return;

      /* Sólo gira mientras se está viendo: no tiene sentido animar algo
         que quedó diez pantallas más arriba. */
      this.visible = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; })
          .observe(this);
      }

      window.addEventListener('resize', () => this.pintar(this.frases[this.actual]));

      this.rodar();
    }

    /* Pone el autor de la frase que toca. Se desvanece y vuelve, para que no
       aparezca de golpe mientras las letras aún están girando. */
    firmar(indice) {
      const autor = this.autores[indice] || '';
      if (this.pie.textContent === autor) return;

      this.pie.classList.add('is-saliendo');
      setTimeout(() => {
        this.pie.textContent = autor;
        this.pie.classList.remove('is-saliendo');
      }, 180);
    }

    /* El tablero tiene siempre tantas tarjetas como la frase más larga. Una
       frase corta, por tanto, deja huecos; si la cabecera va centrada esos
       huecos tienen que repartirse a los dos lados o la frase se ve corrida
       hacia la izquierda. */
    encajar(frase) {
      const largo = Math.max(...this.frases.map((f) => f.length));
      const sobra = largo - frase.length;
      if (sobra <= 0) return frase;

      if (getComputedStyle(this).textAlign !== 'center') return frase.padEnd(largo, ' ');

      const izquierda = Math.floor(sobra / 2);
      return ' '.repeat(izquierda) + frase + ' '.repeat(sobra - izquierda);
    }

    /* Deja el tablero con una tarjeta por letra. */
    pintar(frase) {
      const texto = this.encajar(frase);
      this.tablero.replaceChildren(
        ...Array.from({ length: texto.length }, (_, i) => {
          const c = document.createElement('span');
          c.className = 'pmn-flap__c';
          c.textContent = texto[i] ?? ' ';
          if (c.textContent === ' ') c.classList.add('is-hueco');
          return c;
        })
      );
    }

    async rodar() {
      while (true) {
        await dormir(this.quieto ? 5000 : 3000);
        if (!this.isConnected) return;
        if (!this.visible || document.hidden) continue;

        this.actual = (this.actual + 1) % this.frases.length;
        const frase = this.frases[this.actual];

        this.firmar(this.actual);

        if (this.quieto) {
          this.pintar(frase);
          continue;
        }

        const texto = this.encajar(frase);
        const tarjetas = [...this.tablero.children];
        await Promise.all(
          tarjetas.map((c, i) => this.girar(c, texto[i] ?? ' ', i * RETRASO))
        );
      }
    }

    async girar(tarjeta, destino, espera) {
      await dormir(espera);

      const letras = [];
      for (let i = 0; i < PASOS; i++) {
        letras.push(ALFABETO[Math.floor(Math.random() * ALFABETO.length)]);
      }
      letras.push(destino);

      for (const letra of letras) {
        /* La tarjeta se pone de canto, cambia de letra sin que se vea,
           y vuelve: eso es lo que parece una vuelta de verdad. */
        tarjeta.classList.add('is-girando');
        await dormir(GIRO);

        tarjeta.textContent = letra;
        tarjeta.classList.toggle('is-hueco', letra === ' ');

        tarjeta.classList.add('is-volviendo');
        tarjeta.classList.remove('is-girando');
        /* Hace falta forzar el recálculo entre las dos clases, o el navegador
           las junta y no se ve la vuelta. Antes esto era un
           `requestAnimationFrame`, pero el navegador los suspende cuando la
           pestaña pasa a segundo plano: el giro se quedaba a medias para
           siempre y el tablero se congelaba con letras sueltas. Leer una
           medida es síncrono y no depende de que haya fotogramas. */
        void tarjeta.offsetWidth;
        tarjeta.classList.remove('is-volviendo');
        await dormir(GIRO);
      }
    }
  }

  if (!window.customElements.get('palamano-flap')) {
    window.customElements.define('palamano-flap', PalamanoFlap);
  }
})();
